import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { X, Save, Trash2, Plus, GripVertical, FileText, HelpCircle, Paperclip, Sparkles } from "lucide-react";
import DocumentUploadButton from "@/components/document-upload-button";
import { SopAttachmentsList } from "./sop-attachments";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Placeholder from "@tiptap/extension-placeholder";
import FontFamily from "@tiptap/extension-font-family";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { FontSize } from "@/lib/tiptapExtensions";
import { SopRichEditor } from "./sop-rich-editor";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { preventDismissFromSiblingDialog } from "@/lib/dialog-guards";
import { SopBuilderChat } from "./sop-builder-chat";

interface SopEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sopId: string | null;
}

interface FAQ {
  id?: string;
  question: string;
  answer: string;
  displayOrder: number;
}

function SortableFAQItem({ faq, onEdit, onDelete }: { faq: FAQ, onEdit: () => void, onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: faq.id || faq.question });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-3 p-3 bg-card border rounded-lg mb-2">
      <div {...attributes} {...listeners} className="mt-2 cursor-grab text-muted-foreground hover:text-foreground">
        <GripVertical className="h-4 w-4" />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-sm">{faq.question}</h4>
        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{faq.answer}</p>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="ghost" size="sm" onClick={onEdit} data-testid="button-edit-faq">
          <FileText className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" className="text-destructive" onClick={onDelete} data-testid="button-delete-faq">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default function SopEditorDialog({ open, onOpenChange, sopId }: SopEditorDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("content");
  
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [audience, setAudience] = useState<"manus" | "humans" | "both">("both");
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [visibility, setVisibility] = useState<"all" | "selected">("all");
  const [selectedAssistantIds, setSelectedAssistantIds] = useState<string[]>([]);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [showAiBuilder, setShowAiBuilder] = useState(false);

  const { data: sop, isLoading: isSopLoading } = useQuery<any>({
    queryKey: ["/api/sops", sopId],
    enabled: !!sopId && open,
  });

  const { data: assistants = [] } = useQuery<any[]>({
    queryKey: ["/api/assistants"],
    enabled: open,
  });

  // NOTE: no `= []` default here — a new array ref every render inside the
  // effect deps below would cause an infinite setState loop (React #185).
  const { data: sharesData } = useQuery<{ sharedWithId: string }[]>({
    queryKey: ["/api/sops", sopId, "shares"],
    queryFn: async () => {
      const res = await fetch(`/api/sops/${sopId}/shares`);
      if (!res.ok) throw new Error("Failed to fetch SOP shares");
      return res.json();
    },
    enabled: !!sopId && open,
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        link: false,
        underline: false,
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary underline' } }),
      Image.configure({ HTMLAttributes: { class: 'max-w-full h-auto rounded-lg' } }),
      TextStyle,
      Color,
      FontFamily,
      FontSize,
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      Placeholder.configure({ placeholder: 'Write your SOP content here…' }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: "",
  });

  useEffect(() => {
    if (open) {
      if (sopId && sop) {
        setTitle(sop.title);
        setCategory(sop.category || "");
        setAudience(sop.audience);
        setStatus(sop.status);
        setVisibility(sop.visibility ?? "all");
        setSelectedAssistantIds((sharesData ?? []).map((s) => s.sharedWithId));
        if (editor && sop.content) {
          editor.commands.setContent(sop.content);
        } else if (editor && sop.plainContent) {
          editor.commands.setContent(sop.plainContent);
        } else if (editor) {
          editor.commands.setContent("");
        }
        setFaqs(sop.faqs || []);
      } else if (!sopId) {
        setTitle("");
        setCategory("");
        setAudience("both");
        setStatus("draft");
        setVisibility("all");
        setSelectedAssistantIds([]);
        if (editor) editor.commands.setContent("");
        setFaqs([]);
      }
      setActiveTab("content");
      setEditingFaq(null);
    }
  }, [open, sopId, sop, editor, sharesData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title,
        category: category.trim() || "General",
        audience,
        status,
        visibility,
        content: editor?.getJSON() || {},
        plainContent: editor?.getText() || "",
      };

      let res;
      let newSopId = sopId;
      if (sopId) {
        res = await fetch(`/api/sops/${sopId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`/api/sops`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) throw new Error("Failed to save SOP");
      const savedSop = await res.json();
      newSopId = savedSop.id;

      // Replace the FAQ list atomically in one request — a failure leaves
      // the previously saved FAQs untouched on the server.
      const faqRes = await fetch(`/api/sops/${newSopId}/faqs`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          faqs: faqs.map((faq, i) => ({
            question: faq.question,
            answer: faq.answer,
            displayOrder: i,
          })),
        }),
      });
      if (!faqRes.ok) throw new Error("Failed to save FAQs");
      // Replace the share set atomically in one request — a failure leaves
      // the previous share set untouched on the server (no half-synced list).
      const desiredIds = visibility === "selected" ? selectedAssistantIds : [];
      const sharesRes = await fetch(`/api/sops/${newSopId}/shares`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sharedWithIds: desiredIds }),
      });
      if (!sharesRes.ok) throw new Error("Failed to save SOP shares");
      return newSopId;
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["/api/sops"] });
      if (savedId) {
        queryClient.invalidateQueries({ queryKey: ["/api/sops", savedId] });
        queryClient.invalidateQueries({ queryKey: ["/api/sops", savedId, "shares"] });
      }
      toast({ title: "SOP saved successfully" });
      onOpenChange(false);
    },
    onError: () => {
      toast({ title: "Failed to save SOP", variant: "destructive" });
    }
  });

  const handleSave = () => {
    if (!title.trim()) {
      toast({ title: "Title is required", variant: "destructive" });
      return;
    }
    saveMutation.mutate();
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFaqs((items) => {
        const oldIndex = items.findIndex((i) => (i.id || i.question) === active.id);
        const newIndex = items.findIndex((i) => (i.id || i.question) === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveFaq = () => {
    if (!editingFaq?.question.trim() || !editingFaq?.answer.trim()) return;
    if (editingFaq.id === "new" || !editingFaq.id) {
      setFaqs([...faqs, { ...editingFaq, id: "new-" + Date.now(), displayOrder: faqs.length }]);
    } else {
      setFaqs(faqs.map(f => f.id === editingFaq.id ? editingFaq : f));
    }
    setEditingFaq(null);
  };

  const isSaving = saveMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-hidden flex flex-col p-0"
        onInteractOutside={preventDismissFromSiblingDialog}
      >
        <div className="flex items-center justify-between border-b px-6 py-4 bg-background">
          <div className="flex flex-col">
            <DialogTitle className="text-xl">{sopId ? "Edit SOP" : "Create SOP"}</DialogTitle>
            <DialogDescription>Standard Operating Procedure documentation</DialogDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="button-save-sop">
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? "Saving..." : "Save SOP"}
            </Button>
          </div>
        </div>

        {sopId && isSopLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col md:flex-row bg-muted/20">
            {/* Tabs Sidebar */}
            <div className="w-full md:w-48 lg:w-64 border-r bg-background p-4 flex flex-col gap-2">
              <Button 
                variant={activeTab === "content" ? "secondary" : "ghost"} 
                className="justify-start w-full"
                onClick={() => setActiveTab("content")}
              >
                <FileText className="w-4 h-4 mr-2" /> Content
              </Button>
              <Button 
                variant={activeTab === "faqs" ? "secondary" : "ghost"} 
                className="justify-start w-full"
                onClick={() => setActiveTab("faqs")}
              >
                <HelpCircle className="w-4 h-4 mr-2" /> FAQs
              </Button>
              <Button 
                variant={activeTab === "attachments" ? "secondary" : "ghost"} 
                className="justify-start w-full"
                onClick={() => setActiveTab("attachments")}
                disabled={!sopId}
              >
                <Paperclip className="w-4 h-4 mr-2" /> Attachments
                {!sopId && <span className="text-[10px] ml-auto text-muted-foreground">(Save first)</span>}
              </Button>
            </div>

            {/* Tab Content */}
            <ScrollArea className="flex-1 h-full p-6">
              <div className="max-w-4xl mx-auto pb-20">
                {activeTab === "content" && (
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input 
                        value={title} 
                        onChange={(e) => setTitle(e.target.value)} 
                        placeholder="e.g. Employee Onboarding Process" 
                        data-testid="input-sop-title"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Input
                          value={category}
                          onChange={(e) => setCategory(e.target.value)}
                          placeholder="e.g. General, HR, Operations…"
                          data-testid="input-sop-category"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Audience</Label>
                        <Select value={audience} onValueChange={(v: any) => setAudience(v)}>
                          <SelectTrigger data-testid="select-sop-audience">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="both">Both (AI & Humans)</SelectItem>
                            <SelectItem value="humans">Humans Only</SelectItem>
                            <SelectItem value="manus">AI Assistant Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={status} onValueChange={(v: any) => setStatus(v)}>
                          <SelectTrigger data-testid="select-sop-status">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="published">Published</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {audience !== "manus" && (
                      <div className="space-y-3 p-4 border rounded-lg bg-card">
                        <div className="space-y-2">
                          <Label>Visible to</Label>
                          <Select value={visibility} onValueChange={(v: any) => setVisibility(v)}>
                            <SelectTrigger data-testid="select-sop-visibility">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All assistants</SelectItem>
                              <SelectItem value="selected">Specific assistants</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Controls which human assistants can see this SOP once published. AI assistant access is controlled by the Audience setting.
                          </p>
                        </div>
                        {visibility === "selected" && (
                          <div className="space-y-2">
                            <Label>Assistants</Label>
                            {assistants.length === 0 ? (
                              <p className="text-sm text-muted-foreground">You have no assistants yet.</p>
                            ) : (
                              <div className="space-y-1">
                                {assistants.map((a: any) => {
                                  const id = a.userId;
                                  const name = [a.user?.firstName, a.user?.lastName].filter(Boolean).join(" ") || a.user?.email || id;
                                  const checked = selectedAssistantIds.includes(id);
                                  return (
                                    <label key={id} className="flex items-center gap-2 text-sm cursor-pointer p-1 rounded hover:bg-accent/50">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                          setSelectedAssistantIds((prev) =>
                                            checked ? prev.filter((x) => x !== id) : [...prev, id]
                                          )
                                        }
                                        data-testid={`checkbox-sop-share-${id}`}
                                      />
                                      <span>{name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                            {selectedAssistantIds.length === 0 && (
                              <p className="text-xs text-yellow-600">No assistants selected — no human assistant will see this SOP.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Content</Label>
                      <SopRichEditor editor={editor} />
                    </div>
                  </div>
                )}

                {activeTab === "faqs" && (
                  <div className="space-y-6">
                    {/* AI Builder Panel */}
                    {showAiBuilder && (
                      <div className="h-[520px]">
                        <SopBuilderChat
                          sopTitle={title}
                          existingFaqs={faqs}
                          onAddFaqs={(newFaqs) => {
                            setFaqs((prev) => [
                              ...prev,
                              ...newFaqs.map((f, i) => ({
                                id: "new-" + Date.now() + "-" + i,
                                question: f.question,
                                answer: f.answer,
                                displayOrder: prev.length + i,
                              })),
                            ]);
                          }}
                          onClose={() => setShowAiBuilder(false)}
                        />
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Frequently Asked Questions</h3>
                      <div className="flex items-center gap-2">
                        {!showAiBuilder && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowAiBuilder(true)}
                            data-testid="button-open-sop-ai-builder"
                            className="gap-1.5 border-primary/40 text-primary hover:bg-primary/5"
                          >
                            <Sparkles className="w-3.5 h-3.5" />
                            Build with AI
                          </Button>
                        )}
                        {!editingFaq && (
                          <Button size="sm" onClick={() => setEditingFaq({ id: "new", question: "", answer: "", displayOrder: 0 })} data-testid="button-add-faq">
                            <Plus className="w-4 h-4 mr-2" /> Add FAQ
                          </Button>
                        )}
                      </div>
                    </div>

                    {editingFaq && (
                      <div className="p-4 border rounded-lg bg-card space-y-4">
                        <div className="space-y-2">
                          <Label>Question</Label>
                          <Input 
                            value={editingFaq.question} 
                            onChange={e => setEditingFaq({...editingFaq, question: e.target.value})} 
                            placeholder="What is..."
                            data-testid="input-faq-question"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Answer</Label>
                          <textarea 
                            className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                            value={editingFaq.answer}
                            onChange={e => setEditingFaq({...editingFaq, answer: e.target.value})}
                            placeholder="The answer is..."
                            data-testid="input-faq-answer"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveFaq} data-testid="button-save-faq">Save FAQ</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingFaq(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {faqs.length === 0 && !editingFaq ? (
                      <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
                        No FAQs added yet.
                      </div>
                    ) : (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={faqs.map(f => f.id || f.question)} strategy={verticalListSortingStrategy}>
                          <div className="mt-4">
                            {faqs.map((faq) => (
                              <SortableFAQItem 
                                key={faq.id || faq.question} 
                                faq={faq} 
                                onEdit={() => setEditingFaq(faq)}
                                onDelete={() => setFaqs(faqs.filter(f => f !== faq))}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    )}
                  </div>
                )}

                {activeTab === "attachments" && sopId && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">SOP Documents & Links</h3>
                      <DocumentUploadButton entityType="sop" entityId={sopId} />
                    </div>
                    <SopAttachmentsList sopId={sopId} />
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
